import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function RamsDocumentsPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/rams/search?q=' + encodeURIComponent(searchTerm));
      if (res.ok) {
        const data = await res.json();
        setDocuments(Array.isArray(data) ? data : (data.results || []));
      }
    } catch (err) {
      console.error('Error fetching RAMS documents:', err);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDocuments();
  };

  const statusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      issued: 'bg-blue-100 text-blue-700',
      archived: 'bg-purple-100 text-purple-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const filtered = documents.filter(d => {
    if (statusFilter !== 'all' && d.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="RAMS Documents"
        subtitle="All risk assessments & method statements"
        backTo="/rams"
        backLabel="RAMS"
        sticky
      />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Search + filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 border border-gray-200">
          <div className="flex gap-3 flex-wrap items-center">
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by ref, site, client, equipment..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
            </form>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="issued">Issued</option>
              <option value="archived">Archived</option>
            </select>
            <button
              onClick={fetchDocuments}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold"
            >
              Search
            </button>
          </div>
        </div>

        {/* Documents list */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No RAMS documents found. RAMS are auto-generated when a job is created.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((d) => (
                <div
                  key={d.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/jobs/${d.job_id}/rams`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 text-sm">
                        {d.document_ref || `RAMS #${d.id}`}
                      </h3>
                      <div className="text-xs text-gray-500 mt-1">
                        {d.site_name || d.site_address || '—'} · {d.client_name || '—'}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {d.equipment || '—'} · {d.title || '—'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">
                        {d.assessment_date ? new Date(d.assessment_date).toLocaleDateString('en-GB') : '—'}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${statusBadge(d.status)}`}>
                        {d.status || 'draft'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default RamsDocumentsPage;
